import { useState, useEffect } from 'react';

interface Branding {
  title: string;
  favicon: string;
  description: string;
}

const EMPTY: Branding = { description: '', title: '', favicon: '' };

// Each page mounts its own layout, so this hook remounts on navigation.
// Keep the last payload outside React or the header title (and the sidebar
// width measured from it) flashes empty until the request returns.
let memory: Branding | null = null;
let pending: Promise<Branding | null> | null = null;

function storageKey() {
  return `fleet-branding:${window.location.hostname}`;
}

function readStored(): Branding | null {
  if (memory) return memory;
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Branding>;
    if (!parsed || typeof parsed !== 'object') return null;
    const branding: Branding = {
      description: typeof parsed.description === 'string' ? parsed.description : '',
      title: typeof parsed.title === 'string' ? parsed.title : '',
      favicon: typeof parsed.favicon === 'string' ? parsed.favicon : '',
    };
    memory = branding;
    return branding;
  } catch {
    return null;
  }
}

function remember(branding: Branding) {
  memory = branding;
  try {
    localStorage.setItem(storageKey(), JSON.stringify(branding));
  } catch {
    // Private mode and quota failures can still use the in-memory copy.
  }
}

function setIconLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
}

function applyDocumentBranding(branding: Branding) {
  if (branding.favicon) {
    setIconLink('icon', branding.favicon);
    setIconLink('apple-touch-icon', branding.favicon);
  }
  if (branding.title) {
    document.title = branding.title;
  }
}

function loadBranding(): Promise<Branding | null> {
  if (pending) return pending;
  const hostname = window.location.hostname;
  pending = fetch(`https://raw.githubusercontent.com/rastreosat/rastreosat.github.io/refs/heads/main/${hostname}/info.json`)
    .then(r => {
      if (r.ok) return r.json();
    })
    .then(j => {
      if (!j) return memory;
      const branding: Branding = {
        description: j.description || '',
        title: j.title || '',
        favicon: j.favicon || '',
      };
      remember(branding);
      applyDocumentBranding(branding);
      return branding;
    })
    .catch(() => memory)
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function useBranding() {
  const [branding, setBranding] = useState<Branding>(() => readStored() ?? EMPTY);

  useEffect(() => {
    const stored = readStored();
    if (stored) applyDocumentBranding(stored);
    let active = true;
    loadBranding().then(next => {
      if (active && next) setBranding(next);
    });
    return () => {
      active = false;
    };
  }, []);

  return branding;
}
